import React, { useState, useRef, useEffect, useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom"; 
import { useTranslation, Trans } from 'react-i18next';
import { useAuth } from 'react-oidc-context'; // ✅ ADDED
import TickerInput from "../../TickerInput/TickerInput";
import DataPeriodAlert from "../backtest/partials/DataPeriodAlert"
import styles from "./style/CorrPage.module.css";
import commonStyles from "../../../styles/Common.module.css";
import { ToastContainer, toast } from 'react-toastify';
import { kmeans } from "ml-kmeans";
import { subMonths, format } from "date-fns";
import DOMPurify from 'dompurify'; 

// 💡 분리된 유틸리티 함수들을 import 합니다.
import { generateCorrelationAnalysis } from "./utils/analysisTextGenerator";
import { getInitialStateFromURL } from "./utils/urlStateLoader";
import { 
  computeSSE, 
  findElbow, 
  computeSilhouetteScore 
} from "./utils/clusteringMetrics";
import {
  computeDiversificationBalanceScore,
  computeCorrelationSpreadScore,
  getCorrelationSpreadInterpretation,
  getDiversificationBalanceInterpretation,
  getMarketRiskInterpretation
} from "./utils/scoreCalculator";

// 💡 분리된 UI 컴포넌트들을 import 합니다.
import HeatmapModal from "./components/HeatmapModal";
import ClusterResultBox from "./components/ClusterResultBox";
import CorrelationExamples from "./components/CorrelationExamples";
import PCAPlot from "./components/PCAPlot";

// ✅ ADDED: 포트폴리오 액션 훅 import
import { usePortfolioActions } from '../backtest/hooks/usePortfolioActions';

function Correlation() {

  const { t } = useTranslation();
  const auth = useAuth(); // ✅ ADDED

  // 드롭다운 외부 클릭 감지를 위한 ref
  const dropdownRef = useRef(null);

  const navigate = useNavigate();
  const location = useLocation();
  const initialState = getInitialStateFromURL(location.search);


  const resultsRef = useRef(null);
  const [stocks, setStocks] = useState(initialState.stocks);
  const [error, setError] = useState(null);
    
  useEffect(() => {
    localStorage.setItem("myStocks", JSON.stringify(stocks));
  }, [stocks]);
  
  const [correlationMatrix, setCorrelationMatrix] = useState([]);
  const [showResult, setShowResult] = useState(false);
  const [analysisText, setAnalysisText] = useState("");
  const [commonDates, setCommonDates] = useState([]);

  const [dataPeriodInfo, setDataPeriodInfo] = useState(null);

  const [selectedRange, setSelectedRange] = useState(initialState.selectedRange);
  const [useCustomDate, setUseCustomDate] = useState(initialState.useCustomDate);
  const [startDate, setStartDate] = useState(initialState.startDate);
  const [endDate, setEndDate] = useState(initialState.endDate);

  const [pca, setPca] = useState({ coordinates: [], explainedVariance: [] });
  // 💡 ClusterResultBox가 직접 결과를 받도록 수정 (JSX 렌더링 로직 제거)
  const [kMeansResult, setKMeansResult] = useState(null); 
  const [showHeatmap, setShowHeatmap] = useState(false); 
  const [selectedK, setSelectedK] = useState(initialState.initialK); 
  const [pcaDim, setPcaDim] = useState(2);
  const [userClusterTraces, setUserClusterTraces] = useState([]);
  const [userClusterTraces3D, setUserClusterTraces3D] = useState([]);
  const [marketRiskExposure, setMarketRiskExposure] = useState(null);
  const [diversificationBalanceScore, setDiversificationBalanceScore] = useState(null);
  const [correlationSpreadScore, setCorrelationSpreadScore] = useState(null);
  
  const [showExplanation, setShowExplanation] = useState(false);
  const [showCorrAnalysis, setShowCorrAnalysis] = useState(false);
  const [showPCAImages, setPCAImages] = useState(false);
  const [showSimilarityAnalysis, setShowSimilarityAnalysis] = useState(false);

  const [isLoading, setIsLoading] = useState(false);

  const startAnalyzeRef = useRef(null);
  const analysisRef = useRef(null);
  const similarityAnalysisRef = useRef(null);
  const heatMapRef = useRef(null); 
  const pcaImageRef = useRef(null);

  // =================================================================
  // ✅ ADDED: 포트폴리오 불러오기 로직 설정
  // =================================================================
  // =================================================================
  // ✅ 최종 수정된 포트폴리오 액션 설정 (t와 navigate 인자 추가)
  // =================================================================
  const actions = usePortfolioActions(
    auth, 
    {
      // 1. 종목 데이터를 설정하는 함수
      setRows: (holdings) => {
        const tickerList = holdings
          .map(h => h.ticker)
          .filter(ticker => ticker && ticker.trim() !== "");
        
        setStocks(tickerList);

        // 분석 결과 초기화 로직 (종목이 바뀌었으므로 기존 결과 숨김)
        setCorrelationMatrix([]);
        setPca({ coordinates: [], explainedVariance: [] });
        setAnalysisText("");
        setKMeansResult(null);
        setShowResult(false);
        setShowExplanation(false);
        setShowCorrAnalysis(false);
        setPCAImages(false);
        setShowSimilarityAnalysis(false);
        setShowHeatmap(false);
        setCommonDates([]);
        setSelectedK(null); 
        setError(null);
      },

      // 2. 메시지 처리 함수 (필수)
      setMessage: (msg) => {
        if (msg) console.log("Portfolio Message:", msg);
      },
    },
    t,        // 3번째 인자: 번역 함수 ✅
    navigate  // 4번째 인자: 페이지 이동 함수 ✅ (이게 빠져서 에러 발생)
  );

  const handleLoadPortfolioCustom = (portfolio) => {
    actions.handleLoadPortfolio(portfolio); 
  };

  // ✅ 추가: 외부 클릭 시 드롭다운 닫기 로직
  useEffect(() => {
    const handleClickOutside = (event) => {
      // dropdownRef 영역 밖을 클릭했을 때만 닫기 실행
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        if (actions.setIsLoadModalOpen) {
          actions.setIsLoadModalOpen(false);
        }
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [actions]);

  
  // =================================================================


  const toggleHeatmap = () => setShowHeatmap(prev => !prev);
  
  useEffect(() => {
    if (initialState.isReady) {
      calculateCorrelation(true); 
      
      setShowCorrAnalysis(true);
      setShowSimilarityAnalysis(true);
      setPCAImages(true);
      
      setTimeout(() => {
        resultsRef.current?.scrollIntoView({ behavior: "smooth" });
      }, 1000); 
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const calculateCorrelation = async (skipUrlUpdate = false) => { // 🔄 MODIFIED: async 키워드 추가
    const validAndSanitizedStocks = stocks.filter(stock => stock.trim() !== "").map(stock => {
      const sanitizedStock = DOMPurify.sanitize(stock.toUpperCase(), { USE_PROFILES: { html: false } });
      const cleanedStock = sanitizedStock.replace(/[^A-Z0-9.^-]/g, '');
      return cleanedStock;
    });

    if (validAndSanitizedStocks.length < 3) {
      // 🔄 MODIFIED: alert 대신 error 상태 설정
      // setError(t("correlation.alerts.minStocks")); 
      toast.error(t("correlation.alerts.minStocks"));
      setShowResult(false);
      return;
    }

    if (useCustomDate && new Date(startDate) > new Date(endDate)) {
      // 🔄 MODIFIED: alert 대신 error 상태 설정
      // setError(t("correlation.alerts.invalidDateRange"));
      toast.error(t("correlation.alerts.invalidDateRange"));
      setShowResult(false);
      return;
    }

    setIsLoading(true);
    setError(null); // ✅ ADDED: 분석 시작 시 오류 상태 초기화

     if (!skipUrlUpdate) {
        const stocksString = validAndSanitizedStocks.join(','); 
        const kValue = selectedK !== null ? selectedK.toString() : '';

        const newSearchParams = new URLSearchParams();
        newSearchParams.set('stocks', stocksString);
        newSearchParams.set('range', selectedRange);
        if (useCustomDate) {
            newSearchParams.set('start', startDate);
            newSearchParams.set('end', endDate);
        }
        if (kValue) {
            newSearchParams.set('k', kValue);
        }
        
        navigate(`?${newSearchParams.toString()}`, { replace: true });
    }
    
    const url = `/.netlify/functions/correlation?stocks=${validAndSanitizedStocks.join(",")}&start=${startDate}&end=${endDate}`;
    
    // 🔄 MODIFIED: fetch 로직을 try-catch로 감싸 비동기 오류 처리
    try {
      const res = await fetch(url);

      if (!res.ok) {
        const resultData = await res.json().catch(() => ({}));

        // 🎯 백엔드에서 받은 오류 메시지 (correlation.js)를 추출합니다.
        let errorMessage = res.status === 400
            ? resultData.error || t("correlation.error.dataIssue")
            : res.status === 500
                ? t("chart.serverError")
                : resultData.error || t("chart.fetchError");
                
        throw new Error(errorMessage);
      }
      
      const data = await res.json();
      
      setCorrelationMatrix(data.correlationMatrix);
      setAnalysisText(generateCorrelationAnalysis(stocks, data.correlationMatrix, t));
      setPca(data.pca);
      setCommonDates(data.commonDates); 
      setDataPeriodInfo(data.dataPeriodInfo || null); // 백엔드가 반환하는 dataPeriodInfo를 저장
      setMarketRiskExposure((data.pca.explainedVariance[0] * 100).toFixed(0));
      
      const balanceScore = computeDiversificationBalanceScore(data.pca);
      setDiversificationBalanceScore(balanceScore ? balanceScore : "N/A");
      
      const corrSpread = computeCorrelationSpreadScore(data.correlationMatrix);
      setCorrelationSpreadScore(corrSpread ? corrSpread : "N/A");
      
      setShowResult(true);
      setError(null); // ✅ ADDED: 성공 시 오류 초기화
      
      setTimeout(() => {
        resultsRef.current?.scrollIntoView({ behavior: "smooth" });
      }, 500);

    } catch (err) {
      console.error("Correlation Fetch Error:", err);
      // 🔄 MODIFIED: alert 대신 error 상태 설정
      toast.error(t("correlation.alerts.fetchError"));
      setShowResult(false); // 오류 발생 시 결과 숨김

      // 오류 발생 시 데이터 상태 초기화 (옵션)
      setCorrelationMatrix([]);
      setPca({ coordinates: [], explainedVariance: [] });
      setAnalysisText("");
      setCommonDates([]);
      setDataPeriodInfo(null);
      setKMeansResult(null);
      setError(null);

    } finally {
      setIsLoading(false);
    }
  };

  const copyShareLink = () => {
    if (navigator.clipboard) {
        navigator.clipboard.writeText(window.location.href)
            .then(() => {
                toast.success(t("linkCopied") || "분석 결과 링크가 클립보드에 복사되었습니다. 🔗"); 
            })
            .catch(err => {
                console.error("Failed to copy link: ", err);
                toast.error(t("Failed to copy link"));
            });
    } else {
        toast.warn(t("clipboardNotSupported") || "클립보드 API를 지원하지 않는 브라우저입니다. URL을 직접 복사해주세요.");
    }
  };


  const { calculatedBestK } = useMemo(() => {
    if (pca.coordinates.length === 0) return { calculatedBestK: null };
    const maxK = Math.min(pca.coordinates.length, 4);
    const ks = [], silhouetteValues = [], sseValues = [];
    
    for (let k = 2; k <= maxK; k++) {
      const result = kmeans(pca.coordinates, k, { initialization: "kmeans++", seed: 42 });
      const labels = result.clusters;
      
      const silhouetteScore = computeSilhouetteScore(pca.coordinates, labels);
      const sse = computeSSE(pca.coordinates, k);
      
      ks.push(k);
      silhouetteValues.push(silhouetteScore);
      sseValues.push(sse);
    }
    const bestSilhouetteK = ks[silhouetteValues.indexOf(Math.max(...silhouetteValues))];
    const elbowK = findElbow(ks, sseValues);
    const combinedBestK = Math.round((bestSilhouetteK + elbowK) / 2);
    return { calculatedBestK: combinedBestK };
  }, [pca]);

  useEffect(() => {
        document.title = 'QuanPort-Correlation';
  }, [t]);


  useEffect(() => {
    if (calculatedBestK !== null) setSelectedK(calculatedBestK);
  }, [calculatedBestK]);

  useEffect(() => {
    if (pca.explainedVariance.length >= 2 && stocks.length > 0 && selectedK) {
      // 💡 K-Means 실행 결과 (ClusterResultBox에 직접 전달하기 위해)
      const updatedClustering = kmeans(pca.coordinates, selectedK, { initialization: "kmeans++", seed: 42 });
      setKMeansResult(updatedClustering);
      
      const labels = updatedClustering.clusters;
      const clusters2D = {};
      pca.coordinates.forEach((coord, i) => {
        const label = labels[i];
        if (!clusters2D[label]) clusters2D[label] = { x: [], y: [], texts: [] };
        clusters2D[label].x.push(coord[0]);
        clusters2D[label].y.push(coord[1]);
        clusters2D[label].texts.push(stocks[i]);
      });
      
      // 💡 Plotly 2D Trace 데이터 생성
      setUserClusterTraces(
        Object.keys(clusters2D).map(label => ({
          x: clusters2D[label].x,
          y: clusters2D[label].y,
          mode: "markers+text",
          type: "scatter",
          name: t("correlation.cluster.group", { group: parseInt(label, 10) + 1 }),
          text: clusters2D[label].texts.map(text => DOMPurify.sanitize(text, { USE_PROFILES: { html: false }})),
          textposition: "top center",
          marker: { size: 10 },
        }))
      );
      
      if (pca.coordinates[0].length >= 3) {
        const clusters3D = {};
        pca.coordinates.forEach((coord, i) => {
          const label = labels[i];
          if (!clusters3D[label]) clusters3D[label] = { x: [], y: [], z: [], texts: [] };
          clusters3D[label].x.push(coord[0]);
          clusters3D[label].y.push(coord[1]);
          clusters3D[label].z.push(coord[2]);
          clusters3D[label].texts.push(stocks[i]);
        });
        
        // 💡 Plotly 3D Trace 데이터 생성
        setUserClusterTraces3D(
          Object.keys(clusters3D).map(label => ({
            x: clusters3D[label].x,
            y: clusters3D[label].y,
            z: clusters3D[label].z,
            mode: "markers+text",
            type: "scatter3d",
            name: t("correlation.cluster.group", { group: parseInt(label, 10) + 1 }),
            text: clusters3D[label].texts.map(text => DOMPurify.sanitize(text, { USE_PROFILES: { html: false }})),
            textposition: "top center",
            marker: { size: 5 },
          }))
        );
      } else {
        setUserClusterTraces3D([]);
      }
    }
  }, [pca, stocks, selectedK, pcaDim, t]);

  const loadExample = () => {
    setStocks([]);
    toast.info(t('loadExampleDisclaimer'));
    setTimeout(() => {
      setStocks(["AAPL.US", "TSLA.US", "GLD.US", "0700.HK", "005930.KO"]);
    }, 0);
  };
  
  return (
    <div className={commonStyles.Page}>
      <main className={commonStyles.Container}>
        {isLoading && (
          <div className={styles.loadingOverlay}>
            <div className={styles.doubleBounce}>
              <div className={styles.doubleBounce1}></div>
              <div className={styles.doubleBounce2}></div>
            </div>
          </div>
        )}
        <div className={commonStyles.introContainer}>
          <p className={commonStyles.introMain}>{t("correlation.introMain")}</p>
          <p className={commonStyles.introSub}>{t("correlation.introSub")}</p>
        </div>
        <section className={styles["input-section"]}>
          <h2>{t("enterticker")}</h2>
          <TickerInput
            stocks={stocks}
            onStocksChange={(newStocks) => {
              setStocks(newStocks);
              setCorrelationMatrix([]);
              setPca({ coordinates: [], explainedVariance: [] });
              setAnalysisText("");
              setKMeansResult(null); // 수정: null로 초기화
              setShowResult(false);
              setShowExplanation(false);
              setShowCorrAnalysis(false);
              setPCAImages(false);
              setShowSimilarityAnalysis(false);
              setShowHeatmap(false);
              setCommonDates([]);
              setSelectedK(null); 
              setError(null);
            }}
          />

          <select
            className={styles["date-filter"]}
            value={selectedRange}
            onChange={(e) => {
              setSelectedRange(e.target.value);
              const today = new Date();
              if (e.target.value === "custom") {
                setUseCustomDate(true);
              } else {
                setUseCustomDate(false);
                let start;
                // ... (날짜 범위 설정 로직은 유지)
                switch (e.target.value) {
                 case "6m": start = subMonths(today, 6); break;
                  case "1y": start = subMonths(today, 12); break;
                  case "2y": start = subMonths(today, 24); break;
                  case "3y": start = subMonths(today, 36); break;
                  case "4y": start = subMonths(today, 48); break;  
                  case "5y": start = subMonths(today, 60); break;
                  case "6y": start = subMonths(today, 72); break;
                  case "7y": start = subMonths(today, 84); break;
                  case "8y": start = subMonths(today, 96); break;
                  case "9y": start = subMonths(today, 108); break;
                  case "10y": start = subMonths(today, 120); break;
                  case "20y": start = subMonths(today, 240); break; 
                  case "30y": start = subMonths(today, 360); break;
                  default: start = new Date("2000-01-01");
                }                
                
                setStartDate(format(start, "yyyy-MM-dd")); 
                setEndDate(format(today, "yyyy-MM-dd"));
              }
              setError(null);
            }}
          >
            <option value="6m">{t("correlation.dateRange.sixM")}</option>
            <option value="1y">{t("correlation.dateRange.oneY")}</option>
            <option value="2y">{t("correlation.dateRange.twoY")}</option>
            <option value="3y">{t("correlation.dateRange.threeY")}</option>
            <option value="4y">{t("correlation.dateRange.fourY")}</option>
            <option value="5y">{t("correlation.dateRange.fiveY")}</option>
            <option value="6y">{t("correlation.dateRange.sixY")}</option>
            <option value="7y">{t("correlation.dateRange.sevenY")}</option>
            <option value="8y">{t("correlation.dateRange.eightY")}</option>
            <option value="9y">{t("correlation.dateRange.nineY")}</option>
            <option value="10y">{t("correlation.dateRange.tenY")}</option>
            <option value="20y">{t("correlation.dateRange.twentyY")}</option> 
            <option value="30y">{t("correlation.dateRange.thirtyY")}</option>
            <option value="custom">{t("correlation.dateRange.custom")}</option>
          </select>

          {useCustomDate && (
            <div className={styles["date-range-container"]}>
              <input
                type="date"
                value={startDate}
                onChange={(e) => {
                  setStartDate(e.target.value);
                  setError(null); // ✅ ADDED: 날짜 변경 시 오류 초기화
                }}
              />
              <span> ~ </span>
              <input
                type="date"
                value={endDate}
                onChange={(e) => {
                  setEndDate(e.target.value);
                  setError(null); // ✅ ADDED: 날짜 변경 시 오류 초기화
                }}
              />
            </div>
          )}

          {stocks.length > 0 && (
            <button
              className={styles["alloc-secondary-button"]}
              style={{ marginTop: "0.8rem" }}
              onClick={() => setStocks([])}
            >
              {t("correlation.resetStocks")}
            </button>
          )}
        </section>

        <div className={styles["alloc-intro-actions"]}>
          {/* ✅ ADDED: 포트폴리오 불러오기 버튼 및 목록 */}
          <div 
            ref={dropdownRef}
            style={{ position: 'relative', display: 'inline-block' }}>
            <button
              onClick={() => actions.fetchUserPortfolios(false)} 
              className={styles["alloc-secondary-button"]} 
              style={{ marginRight: "10px", marginBottom: "1rem" }}
              disabled={actions.isPortfoliosLoading || isLoading}
              title={!auth.isAuthenticated ? t('newPortfolioModal.loginRequiredTooltip') || '로그인이 필요합니다.' : ''}
            >
              {actions.isPortfoliosLoading ? t('loading') || '로딩 중...' : t('backtest.loadMyPortfolioButton') || "내 포트폴리오 불러오기"}
            </button>

            {actions.isLoadModalOpen && actions.userPortfolios.length > 0 && (
              <div style={{
                position: 'absolute',
                top: '100%',
                left: 0,
                zIndex: 1000,
                backgroundColor: 'white',
                border: '1px solid #ccc',
                borderRadius: '4px',
                width: '250px',
                maxHeight: '300px',
                overflowY: 'auto',
                boxShadow: '0 2px 5px rgba(0,0,0,0.2)',
                padding: '5px'
              }}>
                <h4 style={{ margin: '5px 10px', fontSize: '0.9em', color: '#666' }}>{t('backtest.selectPortfolio') || '포트폴리오 선택'}</h4>
                <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                  {actions.userPortfolios.map(p => (
                    <li 
                      key={p.portfolioId} 
                      onClick={() => handleLoadPortfolioCustom(p)}
                      style={{
                        padding: '8px 10px',
                        cursor: 'pointer',
                        borderBottom: '1px solid #eee',
                        fontSize: '0.9em'
                      }}
                      onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#f8f9fa'}
                      onMouseOut={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                    >
                      {p.name}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
          {/* ✅ ADDED END */}

          <button
            className={styles["alloc-secondary-button"]}
            style={{ marginBottom: "1rem" }}
            onClick={loadExample}
          >
            {t("loadExample")}
          </button>
          <button className={styles["alloc-primary-button"]} onClick={() => calculateCorrelation()} ref={startAnalyzeRef}>
            {t("correlation.calculate")}
          </button>
        </div>

        <section className={styles["explanation-section"]}>
          {showExplanation && (
            <div className={styles["explanation-text"]}>
              <section className={styles["section"]}>
                <h3>{t("correlation.explanation.title")}</h3>
                <p>{t("correlation.explanation.desc")}</p>
                <article style={{ marginTop: "1rem" }}>
                  {/* 💡 분리된 컴포넌트 사용 */}
                  <CorrelationExamples t={t} />
                </article>
              </section>
            </div>
          )}
        </section>
        <br />
        
        {commonDates.length > 0 && (
          <> {/* Fragment를 사용하여 두 요소를 묶습니다. */}
            <div className={styles["data-period-box"]}>
              <span className={styles["data-period-label"]}>{t("correlation.analysisPeriod.title")}</span>
              <span className={styles["data-period-range"]}>
                {commonDates[0]} ~ {commonDates[commonDates.length - 1]}
              </span>
              <span className={styles["data-period-count"]}>
                ({commonDates.length}{t("correlation.day")})
              </span>
              <span className={styles["data-period-tooltip"]}>
                ℹ️
                <span className={styles["tooltip-text"]}>
                {t("correlation.analysisPeriod.tooltip")}
                </span>
              </span>
            </div>
            
            {/* dataPeriodInfo와 isPeriodLimited 조건도 유지하면서, 
                commonDates.length > 0일 때만 렌더링되도록 상위 조건에 포함됩니다. */}
            {dataPeriodInfo && dataPeriodInfo.isPeriodLimited && (
              <DataPeriodAlert 
                  dataPeriodInfo={dataPeriodInfo} 
                  t={t}
              />
            )}
          </>
        )}

        {/* ================================================================= */}
        {/* 🔄 3. 로딩 및 오류 상태에 따른 조건부 렌더링 (ResultChart.js 스타일) */}
        {/* ================================================================= */}
        {isLoading ? (
          // 1. 로딩 중일 때
          <div className={commonStyles.loadingContainer} style={{ margin: "40px auto" }}>
            <div className={commonStyles.spinner}></div>
            {t("dataLoadingCalculatiing") || '데이터를 불러와 상관 관계를 분석 중입니다...'}
          </div>
        ) : showResult && correlationMatrix.length > 0 && pca.explainedVariance.length >= 2 ? (
          // 3. 정상적으로 데이터가 로드되었을 때 (기존 결과 섹션)
          <section ref={resultsRef} className={styles["results-section"]}>
            <div className={styles["result-summary"]}>
              <h2>{t("correlation.diagnosis.title")}</h2>
              <p className={styles["correlationDiagnosisSubtitle"]}>
                {t("correlation.diagnosis.marketRiskExposure", { exposure: marketRiskExposure })}
              </p>
              <p>→ {getMarketRiskInterpretation(Number(marketRiskExposure), t)}</p>
              <p className={styles["correlationDiagnosisSubtitle"]}>
                {t("correlation.diagnosis.diversificationBalanceScore", { score: diversificationBalanceScore })}
              </p>
              <p>→ {getDiversificationBalanceInterpretation(diversificationBalanceScore, t)}</p>
              <p className={styles["correlationDiagnosisSubtitle"]}>
                {t("correlation.diagnosis.correlationSpreadScore", { score: correlationSpreadScore })}
              </p>
              <p>→ {getCorrelationSpreadInterpretation(correlationSpreadScore, t)}</p>

              <div style={{ display: 'flex', gap: '10px', marginTop: '15px' }}>
                <button
                  className={styles["explanation-button"]}
                  onClick={() => {
                    setShowCorrAnalysis(true);
                    setTimeout(() => {
                      analysisRef.current?.scrollIntoView({ behavior: "smooth" });
                    }, 500);
                  }}
                >
                  {t("correlation.viewDetail")}
                </button>
                <button
                  className={styles["alloc-secondary-button"]}
                  onClick={copyShareLink}
                >
                  {t("shareLink") || "결과 공유 링크 복사"} 
                </button>
              </div>
            </div>

            {showCorrAnalysis && (
              <>
                <div className={styles["analysis-text"]} ref={analysisRef}>
                  <h2>{t("correlation.resultTitle")}</h2>
                  <pre dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(analysisText) }} /> 

                  <button
                      className={styles["alloc-secondary-button"]}
                      style={{ marginTop: "15px", marginBottom: "15px" }}
                      onClick={toggleHeatmap}
                  >
                      {t("correlation.heatmap.show")}
                  </button>

                  <button
                    className={styles["alloc-primary-button"]}
                    onClick={() => {
                      setShowSimilarityAnalysis(true);
                      setTimeout(() => {
                        similarityAnalysisRef.current?.scrollIntoView({ behavior: "smooth" });
                      }, 600);
                    }}
                  >
                    {t("correlation.groupSimilarity")}
                  </button>
                </div>
                
                <br />

                {showSimilarityAnalysis && (
                  <>
                    <div className={styles["analysis-text"]} ref={similarityAnalysisRef}>
                      <h2>{t("correlation.similarityAnalysis.title")}</h2>
                      <ul className={styles["pca-plot-explanation"]} ref={pcaImageRef}>
                        <li>  
                          <Trans
                            i18nKey="correlation.pca.explanation.main"
                            components={{ bold: <b /> }}
                            values={{
                              value:
                                pcaDim === 2
                                  ? ((pca.explainedVariance[0] + pca.explainedVariance[1]) * 100).toFixed(0) +
                                    "%"
                                  : ((pca.explainedVariance[0] +
                                      pca.explainedVariance[1] +
                                      pca.explainedVariance[2]) *
                                      100).toFixed(0) + "%",
                            }}
                          />
                        </li>
                      </ul>
                      
                      {/* 💡 분리된 컴포넌트 사용 */}
                      <PCAPlot 
                        pca={pca} 
                        pcaDim={pcaDim} 
                        userClusterTraces={userClusterTraces} 
                        userClusterTraces3D={userClusterTraces3D} 
                        t={t} 
                      />
                      
                      {/* 💡 클러스터링 결과 박스 렌더링 */}
                      {/* {kMeansResult && (
                         <ClusterResultBox 
                            pca={pca} 
                            stocks={stocks} 
                            clusteringResult={kMeansResult} 
                            t={t}
                          />
                      )} */}
                      
                      <div className={styles["pca-visual-buttons"]}>
                        <button
                          className={`${styles["alloc-primary-button"]} ${pcaDim === 2 ? styles["active"] : ""}`}
                          onClick={() => {
                            setPcaDim(2);
                            setPCAImages(true);
                            setTimeout(() => {
                              pcaImageRef.current?.scrollIntoView({ behavior: "smooth" });
                            }, 500);
                          }}
                        >
                          {t("correlation.pca.buttons.2d")}
                        </button>
                        {pca.coordinates[0]?.length >= 3 && (
                          <button
                            className={`${styles["alloc-primary-button"]} ${pcaDim === 3 ? styles["active"] : ""}`}
                            onClick={() => {
                              setPcaDim(3);
                              setPCAImages(true);
                              setTimeout(() => {
                                pcaImageRef.current?.scrollIntoView({ behavior: "smooth" });
                              }, 500);
                            }}
                          >
                            {t("correlation.pca.buttons.3d")}
                          </button>
                        )}
                      </div>
                    </div>
                  </>
                )}
              </>
            )}
          </section>
        ) : null} 
      </main>
      
      {/* 💡 분리된 컴포넌트 사용 */}
      <HeatmapModal 
        stocks={stocks} 
        correlationMatrix={correlationMatrix} 
        show={showHeatmap} 
        onClose={toggleHeatmap} 
        t={t}
      />
      
      <ToastContainer
        autoClose={5000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        style={{
          position: 'fixed',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          zIndex: 9999,
          border: 'none', 
        }}
      />
    </div>
  );
}

export default Correlation;